import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Payment,
  Check,
  Close,
  Refresh,
  AccountBalance,
  CreditCard,
  Security,
  ArrowForward,
} from '@mui/icons-material';
import {
  getStripeConnectStatus,
  startStripeOnboarding,
  refreshStripeStatus,
} from '../../services/api/partnerService';
import { StripeConnectStatus } from '../../types';

const PartnerStripePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStripeConnectStatus();
      setStatus(data);
    } catch (err: any) {
      console.error('Status load error:', err);
      setError(err.response?.data?.detail || 'Stripe接続状態の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/partner/settings/stripe`;
      const refreshUrl = `${window.location.origin}/partner/settings/stripe?refresh=true`;
      const result = await startStripeOnboarding(returnUrl, refreshUrl);
      if (result.onboarding_url) {
        window.location.href = result.onboarding_url;
      }
    } catch (err: any) {
      console.error('Onboarding error:', err);
      setError(err.response?.data?.detail || 'Stripeオンボーディングの開始に失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await refreshStripeStatus();
      await loadStatus();
    } catch (err: any) {
      console.error('Refresh error:', err);
      setError(err.response?.data?.detail || '状態の更新に失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isFullyConnected = status?.charges_enabled && status?.payouts_enabled && status?.onboarding_complete;

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Stripe連携設定
        </Typography>
        <Typography variant="body2" color="text.secondary">
          売上を受け取るためのStripe Connect設定
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 接続状態カード */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Payment sx={{ fontSize: 40, color: isFullyConnected ? 'success.main' : 'warning.main' }} />
                  <Box>
                    <Typography variant="h6">
                      Stripe Connect
                    </Typography>
                    <Chip
                      label={isFullyConnected ? '接続済み' : status?.is_connected ? '設定中' : '未接続'}
                      color={isFullyConnected ? 'success' : status?.is_connected ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <Button
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={actionLoading}
                >
                  更新
                </Button>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* 接続ステップ */}
              <List>
                <ListItem>
                  <ListItemIcon>
                    {status?.is_connected ? (
                      <Check color="success" />
                    ) : (
                      <Close color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="アカウント作成"
                    secondary="Stripe Connectアカウントが作成されました"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {status?.details_submitted ? (
                      <Check color="success" />
                    ) : (
                      <Close color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="情報登録"
                    secondary="ビジネス情報と本人確認を完了"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {status?.charges_enabled ? (
                      <Check color="success" />
                    ) : (
                      <Close color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="決済受付"
                    secondary="顧客からの決済を受け付け可能"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {status?.payouts_enabled ? (
                      <Check color="success" />
                    ) : (
                      <Close color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="出金"
                    secondary="銀行口座への出金が可能"
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              {/* アクションボタン */}
              {!isFullyConnected && (
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartOnboarding}
                    disabled={actionLoading}
                    startIcon={actionLoading ? <CircularProgress size={20} /> : <ArrowForward />}
                    sx={{ px: 4 }}
                  >
                    {status?.is_connected ? 'オンボーディングを続ける' : 'Stripe連携を開始'}
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Stripeの画面に移動して、ビジネス情報と銀行口座を登録します
                  </Typography>
                </Box>
              )}

              {isFullyConnected && (
                <Alert severity="success" icon={<Check />}>
                  Stripe Connectの設定が完了しました。売上の受け取りが可能です。
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* サイドバー */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalance color="primary" />
                <Typography variant="h6">売上の流れ</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                1. 顧客が決済リンクで購入
              </Typography>
              <Typography variant="body2" color="text.secondary">
                2. 売上がGambitに入金
              </Typography>
              <Typography variant="body2" color="text.secondary">
                3. 月末に利用状況を集計
              </Typography>
              <Typography variant="body2" color="text.secondary">
                4. 翌月に売上をお支払い
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CreditCard color="primary" />
                <Typography variant="h6">必要な情報</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                • 法人または個人事業主の情報
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                • 代表者の本人確認書類
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 銀行口座情報
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Security color="primary" />
                <Typography variant="h6">セキュリティ</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Stripe Connectは世界中の企業が利用する安全な決済プラットフォームです。
                お客様の情報はStripeによって保護されます。
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerStripePage;
